import { ProcessingLenderStrategyType, ProcessingMode, StopCondition } from '@nemuru-eng/js-api-client';
import { ChooseStrategyType } from '@nemuru-eng/js-api-client/slim';
import { z } from 'zod';

import { OrchestrationConfig } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OrchestrationConfig';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';

export const OrchestrationFormSchema = (t: GetTextIdTranslationType) =>
  z
    .object({
      processingStrategy: z.nativeEnum(ProcessingLenderStrategyType),
      processingMode: z.nativeEnum(ProcessingMode),
      stopCondition: z.nativeEnum(StopCondition),
      chooseStrategyType: z.nativeEnum(ChooseStrategyType),
      lenderName: z.string().nullable(),
    })
    .superRefine((values, context) => {
      // In waterfall the borrower never sees several offers at once. The api rejects it too.
      if (
        values.processingStrategy === ProcessingLenderStrategyType.WATERFALL &&
        values.chooseStrategyType === ChooseStrategyType.BORROWER_SELECTION
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['chooseStrategyType'],
          message: t('LENDER_ORCHESTRATION_ERROR_WATERFALL_BORROWER'),
        });
      }
      if (values.chooseStrategyType === ChooseStrategyType.PRIORITY_LENDER_THEN_PRICE && !values.lenderName) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['lenderName'], message: t('REQUIRED_FIELD') });
      }
    });

export type OrchestrationFormType = z.infer<ReturnType<typeof OrchestrationFormSchema>>;

export const fromOrchestrationConfig = (config: OrchestrationConfig): OrchestrationFormType => ({
  processingStrategy: config.processingStrategy,
  processingMode: config.processingMode,
  stopCondition: config.stopCondition,
  chooseStrategyType: config.chooseStrategyType,
  lenderName: config.chooseStrategyTypeOptions?.lenderName ?? null,
});

export const toOrchestrationConfig = (values: OrchestrationFormType): OrchestrationConfig => ({
  processingStrategy: values.processingStrategy,
  processingMode: values.processingMode,
  stopCondition: values.stopCondition,
  chooseStrategyType: values.chooseStrategyType,
  chooseStrategyTypeOptions:
    values.chooseStrategyType === ChooseStrategyType.PRIORITY_LENDER_THEN_PRICE
      ? { lenderName: values.lenderName ?? undefined }
      : null,
});
